import React, { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { streams } from '../utils/streamUtils';

const FFT_SIZE = 256;
const CANVAS_OPACITY = 0.6;
const MAX_RADIUS = 50;
const ICON_SIZE = 48;
const CANVAS_SIZE = ICON_SIZE * 1;

interface MicIconWithVisualizationProps {
  deviceId: string;
  className?: string;
}

const MicIconWithVisualization: React.FC<MicIconWithVisualizationProps> = ({
  deviceId,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const animationIdRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [hasAudioInput, setHasAudioInput] = useState(false);
  const createdTracksRef = useRef<MediaStreamTrack[]>([]); // Track all created tracks for cleanup

  useEffect(() => {
    let audioTrack: MediaStreamTrack | null = null;

    const stopVisualization = () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const canvasCtx = canvas.getContext('2d')!;
        canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      // Stop all tracks that were created by this component
      createdTracksRef.current.forEach((track) => {
        if (track.readyState === 'live') {
          track.stop();
        }
      });
      createdTracksRef.current = [];
      analyzerRef.current = null;
      setHasAudioInput(false);
    };

    const startVisualization = async () => {
      if (!audioTrack || !canvasRef.current) return;

      try {
        const audioContext = new AudioContext();
        await audioContext.resume();

        const analyzer = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(
          new MediaStream([audioTrack]),
        );
        source.connect(analyzer);

        analyzer.fftSize = FFT_SIZE;
        const bufferLength = analyzer.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const canvas = canvasRef.current;
        const canvasCtx = canvas.getContext('2d')!;
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        const draw = () => {
          if (!canvasRef.current || !analyzerRef.current) return;

          animationIdRef.current = requestAnimationFrame(draw);

          analyzer.getByteFrequencyData(dataArray);

          canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

          // Calculate average volume for smoother visualization
          let sum = 0;
          for (let i = 0; i < bufferLength; i += 1) {
            sum += dataArray[i];
          }
          const average = sum / bufferLength;
          const normalizedVolume = average / 255;

          // Create pulsing circles with gradient
          const baseRadius = 10;
          const maxPulseRadius = MAX_RADIUS * normalizedVolume;

          // Draw 3 expanding circles for a ripple effect
          for (let j = 0; j < 3; j += 1) {
            const offset = (Date.now() / 800 + j * 0.4) % 1;
            const radius = baseRadius + maxPulseRadius * offset;
            const alpha = (1 - offset) * normalizedVolume;

            canvasCtx.beginPath();
            canvasCtx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false);
            canvasCtx.strokeStyle = '#1f2937'; // Gray-800 for much darker look
            canvasCtx.lineWidth = 2;
            canvasCtx.globalAlpha = alpha * 0.8;
            canvasCtx.stroke();
          }

          canvasCtx.globalAlpha = 1;
        };

        audioContextRef.current = audioContext;
        analyzerRef.current = analyzer;
        setHasAudioInput(true);

        draw();
      } catch (error) {
        console.error('Error starting audio visualization:', error);
        stopVisualization();
      }
    };

    const setupAudioStream = async () => {
      stopVisualization();

      if (deviceId) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId },
            video: false,
          });
          streamRef.current = stream;
          audioTrack = stream.getAudioTracks()[0] || null;
          // Track all created tracks for proper cleanup
          if (audioTrack) {
            createdTracksRef.current.push(audioTrack);
          }
        } catch (error) {
          console.error('Error setting up device audio stream:', error);
          stopVisualization();
          return;
        }
      } else {
        // Use userMediaStream when no deviceId is provided
        const userMediaTracks = streams.userMediaStream.getAudioTracks();
        if (userMediaTracks.length > 0) {
          // eslint-disable-next-line prefer-destructuring
          audioTrack = userMediaTracks[0];
        } else {
          // If no audio track in userMediaStream, try to get default audio
          try {
            const defaultStream = await navigator.mediaDevices.getUserMedia({
              audio: true,
            });
            audioTrack = defaultStream.getAudioTracks()[0] || null;
            if (audioTrack) {
              streams.userMediaStream.addTrack(audioTrack);
              // Track the newly created track for proper cleanup
              createdTracksRef.current.push(audioTrack);
            }
          } catch (error) {
            console.error('Error getting default audio stream:', error);
            stopVisualization();
            return;
          }
        }
      }

      if (audioTrack) {
        startVisualization();
        audioTrack.addEventListener('ended', stopVisualization);
        audioTrack.addEventListener('mute', stopVisualization);
        audioTrack.addEventListener('unmute', startVisualization);
      }
    };

    setupAudioStream();

    return () => {
      stopVisualization();
      if (audioTrack) {
        audioTrack.removeEventListener('ended', stopVisualization);
        audioTrack.removeEventListener('mute', stopVisualization);
        audioTrack.removeEventListener('unmute', startVisualization);
      }
    };
  }, [deviceId]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        width: '3rem',
        height: '3rem',
        marginLeft: deviceId ? '-16px' : '0px',
      }}
    >
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          zIndex: 0,
          opacity: CANVAS_OPACITY,
        }}
      />
      <Mic
        className={className}
        style={{
          position: 'relative',
          zIndex: 1,
          animation: !hasAudioInput
            ? 'pulse 1.5s ease-in-out infinite'
            : 'none',
        }}
      />
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.7; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default MicIconWithVisualization;
